import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertController, IonInput, ModalController, NavController, PopoverController } from '@ionic/angular';

import { EditEmailChannelComponent } from 'src/app/channels/email/edit-email-channel.component';
import { EditMsTeamsChannelComponent } from 'src/app/channels/ms-teams/edit-ms-teams-channel.component';
import { EditWidgetChannelComponent } from 'src/app/channels/widget/edit-widget-channel.component';
import {
  ActionDropdownComponent,
  ActionDropdownType,
} from 'src/app/components/action-dropdown/action-dropdown.component';
import { ExternalCrmAccountSelectorModalComponent } from 'src/app/components/external-crm-account-selector/external-crm-account-selector-modal/external-crm-account-selector-modal.component';
import {
  INBOX_TYPES,
  INTEGRATIONS,
  PRIVATE_CHANNEL_NAME,
  PROJECT_MEMBER_ROLES,
  SUPPORT_STEP_ASSIGNEE_TYPES,
  routes,
} from 'src/app/constants';
import { generateCustomFieldsArray, getSlackLink, removeUndefinedFields } from 'src/app/utils/utils';
import { Customer, Tag } from 'src/models';
import { CHANNEL_TYPES } from 'src/models/channels';
import { Project, PROJECT_VISIBILITY } from 'src/models/projects';
import { AuthService } from 'src/services/auth.service';
import { CustomersService } from 'src/services/customers.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';
import { TagsService } from 'src/services/tags.service';
import { UserGroupsService } from 'src/services/user-groups.service';
import { UsersService } from 'src/services/users.service';

@Component({
  selector: 'app-customer-admin',
  templateUrl: './customer-admin.page.html',
  styleUrls: ['./customer-admin.page.scss'],
})
export class CustomerAdminPage {
  @ViewChild('newCustomerNameInput', { static: false }) newCustomerNameInput: IonInput;
  @ViewChild('customerNameInput', { static: false }) customerNameInput: IonInput;

  public appPages;
  public customerId;
  public customer: Customer;
  public customFieldValues: any[];
  public customFieldOptions: any[];
  primarySupportAssigneeId?: string;
  primarySupportAssigneeType?: string;
  primarySupportAssigneeName?: string;
  secondarySupportAssigneeId?: string;
  secondarySupportAssigneeType?: string;
  secondarySupportAssigneeName?: string;
  connectedCrm: string;
  isShowingCustomFieldsModal = false;
  isSavingCustomFields = false;

  public newCustomerName: string;
  public isSavingNewCustomer: boolean;
  public project: Partial<Project>;

  PROJECT_VISIBILITY = PROJECT_VISIBILITY;
  CHANNEL_TYPES = CHANNEL_TYPES;
  PRIVATE_CHANNEL_NAME = PRIVATE_CHANNEL_NAME;

  constructor(
    route: ActivatedRoute,
    public authService: AuthService,
    private customersService: CustomersService,
    private searchablePopoverService: SearchablePopoverService,
    private tagsService: TagsService,
    private userGroupsService: UserGroupsService,
    private usersService: UsersService,
    private msgSrvc: MessageService,
    private alertCtrl: AlertController,
    private modalCtrl: ModalController,
    private popoverCtrl: PopoverController,
    private navCtrl: NavController,
    private router: Router,
  ) {
    this.appPages = [];
    this.customerId = route.snapshot.paramMap.get('customerId');
    if (this.authService.tenant.integrations[INTEGRATIONS.salesforce.key]?.enabled) {
      this.connectedCrm = 'Salesforce';
    } else if (this.authService.tenant.integrations[INTEGRATIONS.hubspot.key]?.enabled) {
      this.connectedCrm = 'HubSpot';
    }

    if (!this.customerId && this.authService.projects?.length === 1) {
      this.project = this.authService.projects[0];
    }

    if (this.customerId) {
      if (this.authService.tenant.accountsEnabled) {
        // redirect to channels list page instead
        this.router.navigate([`/dashboard/channels/${this.customerId}/members`]);
        return;
      }

      this.getCustomer();
    } else {
      setTimeout(() => {
        this.newCustomerNameInput.setFocus();
      }, 500);
    }

    this.customFieldOptions = this.authService.tenant.customCustomerFields ?? [];
  }

  async getCustomer() {
    this.customersService.get(this.customerId).then(async (customer: Customer) => {
      this.customer = customer;

      this.primarySupportAssigneeId = this.customer.primarySupportAssigneeId;
      this.primarySupportAssigneeType = this.customer.primarySupportAssigneeType;
      this.secondarySupportAssigneeId = this.customer.secondarySupportAssigneeId;
      this.secondarySupportAssigneeType = this.customer.secondarySupportAssigneeType;

      this.customFieldValues = generateCustomFieldsArray(
        this.customFieldOptions ?? [],
        this.customer.customFields ?? [],
        {},
      );

      this.loadAssignees();

      this.appPages = [
        {
          title: 'Overview',
          url: `/dashboard/customers/${this.customerId}/overview`,
          icon: 'pulse',
        },
      ];

      if (customer.slackChannelId) {
        this.appPages.push({
          title: 'Members',
          url: `/dashboard/customers/${this.customerId}/members`,
          icon: 'people',
        });
      }

      const projectMember = this.authService.projects.find((p) => p.id === this.customer?.projectId);
      if (
        (customer.adminIds ?? []).includes(this.authService.userId) ||
        this.authService.isTenantAdmin ||
        projectMember?.role === PROJECT_MEMBER_ROLES.OWNER
      ) {
        this.appPages.push(
          // {
          //   title: 'Templates',
          //   url: `/dashboard/customers/${this.customerId}/templates`,
          //   icon: 'bookmarks',
          // },
          // {
          //   title: 'Outbound',
          //   url: `/dashboard/customers/${this.customerId}/outbound`,
          //   icon: 'send',
          // },
          {
            title: 'Settings',
            url: `/dashboard/customers/${this.customerId}/settings`,
            icon: 'cog',
          },
        );
      }
    });
  }

  async loadAssignees() {
    if (this.primarySupportAssigneeId) {
      if (this.primarySupportAssigneeType !== SUPPORT_STEP_ASSIGNEE_TYPES.USER) {
        const group = await this.userGroupsService.getById(this.primarySupportAssigneeId);
        this.primarySupportAssigneeName = `${group.name} (Group)`;
      } else if (this.primarySupportAssigneeType === SUPPORT_STEP_ASSIGNEE_TYPES.USER) {
        const user = await this.usersService.getUser(this.primarySupportAssigneeId);
        this.primarySupportAssigneeName = user.name;
      }
    }

    if (this.secondarySupportAssigneeId) {
      if (this.secondarySupportAssigneeType !== SUPPORT_STEP_ASSIGNEE_TYPES.USER) {
        const group = await this.userGroupsService.getById(this.secondarySupportAssigneeId);
        this.secondarySupportAssigneeName = `${group.name} (Group)`;
      } else if (this.secondarySupportAssigneeType === SUPPORT_STEP_ASSIGNEE_TYPES.USER) {
        const user = await this.usersService.getUser(this.secondarySupportAssigneeId);
        this.secondarySupportAssigneeName = user.name;
      }
    }

    if (!this.primarySupportAssigneeName) {
      this.primarySupportAssigneeName = 'No one';
    }
    if (!this.secondarySupportAssigneeName) {
      this.secondarySupportAssigneeName = 'No one';
    }
  }

  showAssigneeOptions(isPrimary: boolean) {
    this.searchablePopoverService.assignees({
      event,
      selectedId: isPrimary ? this.primarySupportAssigneeId : this.secondarySupportAssigneeId,
      showCreatedByUserOption: false,
      showPrimaryRepOption: false,
      showNoneOption: true,
      callback: async (id: string, name: string, object: any) => {
        if (isPrimary) {
          this.primarySupportAssigneeType = object?.type ?? SUPPORT_STEP_ASSIGNEE_TYPES.USER;
          this.primarySupportAssigneeName = !id || id === this.customer.primarySupportAssigneeId ? null : name;
          this.primarySupportAssigneeId = !id || id === this.customer.primarySupportAssigneeId ? null : id;
        } else {
          this.secondarySupportAssigneeType = object?.type ?? SUPPORT_STEP_ASSIGNEE_TYPES.USER;
          this.secondarySupportAssigneeName = !id || id === this.customer.secondarySupportAssigneeId ? null : name;
          this.secondarySupportAssigneeId = !id || id === this.customer.secondarySupportAssigneeId ? null : id;
        }

        this.save();
      },
    });
  }

  async saveEditedName() {
    try {
      await this.customersService.update(this.customerId, {
        name: this.customer.name,
      });
      this.msgSrvc.show('Updated');
    } catch (e) {
      this.msgSrvc.showError(e);
    }
  }

  async editTag(tag: Tag) {
    const options: ActionDropdownType[] = [
      {
        label: 'Remove tag',
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      event,
      componentProps: {
        options,
        callback: async (idx: number) => {
          popover.dismiss();

          await this.tagsService.deleteCustomerLinks(tag.id, [this.customerId]);

          this.getCustomer();
        },
      },
    });
    popover.present();
  }

  addTag() {
    this.searchablePopoverService.tags({
      event,
      projectId: this.customer?.projectId ?? this.project?.id,
      callback: async (id: string) => {
        await this.tagsService.createCustomerLinks(id, [this.customerId]);

        this.getCustomer();
      },
    });
  }

  async linkCrm() {
    const modal = await this.modalCtrl.create({
      component: ExternalCrmAccountSelectorModalComponent,
      componentProps: {
        externalCrmType: this.connectedCrm,
      },
    });

    modal.present();

    modal.onDidDismiss().then(async ({ data }: any) => {
      if (data?.externalCrmAccount?.id) {
        await this.customersService.update(this.customerId, {
          externalCrmMetadata: { id: data.externalCrmAccount.id },
        });
        this.getCustomer();
      }
    });
  }

  async unlinkCrm() {
    await this.customersService.update(this.customerId, {
      externalCrmMetadata: { id: null },
    });

    this.getCustomer();
  }

  openSlackChannel() {
    window.open(
      getSlackLink(
        this.customer.slackTeamId ?? this.authService.tenant.slackTeamId,
        null,
        null,
        this.customer.slackChannel.id,
      ),
      '_blank',
    );
  }

  viewInbox() {
    const filter = [
      [
        { label: 'Customer', id: 'customerId', icon: 'business', type: 'select' },
        [{ id: this.customerId, label: this.customer.name }],
      ],
    ];
    this.navCtrl.navigateRoot(`${routes.DASHBOARD}/${routes.INBOX}/${INBOX_TYPES.ALL}`, {
      queryParams: {
        filters: JSON.stringify(filter),
      },
      queryParamsHandling: 'merge',
    });
  }

  composeSlackBroadcast() {
    this.navCtrl.navigateForward(`${routes.SLACK_MESSAGE}/new`, {
      queryParams: removeUndefinedFields({
        customerIds: this.customerId,
      }),
    });
  }

  async delete() {
    const subHeader = "We'll delete this customer, though conversations will still be visible in the dashboard";
    const alert = await this.alertCtrl.create({
      header: 'Are you sure?',
      subHeader,
      buttons: [
        {
          text: 'Nevermind',
        },
        {
          text: 'Do it',
          handler: async () => {
            try {
              await this.customersService.delete(this.customerId);
              this.navCtrl.navigateRoot(`${routes.DASHBOARD}/customers`);
            } catch (e) {
              this.msgSrvc.showError(e);
            }
          },
        },
      ],
    });
    alert.present();
  }

  async showOptions() {
    const DELETE_LABEL = 'Delete profile';
    const RENAME_LABEL = 'Rename';

    const options: ActionDropdownType[] = [
      {
        label: RENAME_LABEL,
        icon: 'create-outline',
      },
      {
        label: DELETE_LABEL,
        icon: 'trash-outline',
        destructive: true,
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: async (_index: number, label: string) => {
          popover.dismiss();

          if (label === DELETE_LABEL) {
            this.delete();
          } else if (label === RENAME_LABEL) {
            this.customerNameInput.setFocus();
          }
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  manageCustomFields() {
    this.isShowingCustomFieldsModal = true;
  }

  onDismissCustomFieldsModal() {
    this.isShowingCustomFieldsModal = false;
  }

  didUpdateCustomFields() {
    this.save();
  }

  async updateDefaultCustomFields() {
    this.isSavingCustomFields = true;
    await this.authService.updateTenant({
      customCustomerFields: this.customFieldOptions,
    });
    this.isSavingCustomFields = false;
    this.isShowingCustomFieldsModal = false;

    this.customFieldValues = generateCustomFieldsArray(
      this.customFieldOptions ?? [],
      this.customer.customFields ?? [],
      {},
    );
  }

  async addAdmin() {
    this.searchablePopoverService.users({
      event,
      showNoneOption: false,
      callback: async (_id: string, _name: string, user: any) => {
        if (user.email) {
          this.customer.adminEmails.push(user.email);
          await this.customersService.update(this.customerId, { adminEmails: this.customer.adminEmails });
        } else {
          this.msgSrvc.show(`This user doesn't have a valid email address on file`);
        }
      },
    });
  }

  async deleteAdminEmailAtIndex(index: number) {
    this.customer.adminEmails.splice(index, 1);
    await this.customersService.update(this.customerId, { adminEmails: this.customer.adminEmails });
  }

  async save() {
    const updates: Partial<Customer> = removeUndefinedFields({
      primarySupportAssigneeId: this.primarySupportAssigneeId,
      primarySupportAssigneeType: this.primarySupportAssigneeType,
      secondarySupportAssigneeId: this.secondarySupportAssigneeId,
      secondarySupportAssigneeType: this.secondarySupportAssigneeType,
      customFields: this.customFieldValues,
      projectId: this.project?.id,
    });

    try {
      await this.customersService.update(this.customerId, updates);

      await this.getCustomer();

      this.msgSrvc.show('✔︎ Updated');
    } catch (err) {
      console.log(err);

      this.msgSrvc.show('Something went wrong');
    }
  }

  async createNewCustomer() {
    if (!this.newCustomerName) {
      return;
    }

    if (!this.project?.id) {
      this.msgSrvc.show('Please select a project');

      return;
    }

    this.isSavingCustomFields = true;
    const result = await this.customersService.create(this.newCustomerName);
    this.navCtrl.navigateRoot(`${routes.DASHBOARD}/${routes.CUSTOMERS}/${result.id}/overview`);
    this.isSavingCustomFields = false;
  }

  selectProject() {
    this.searchablePopoverService.projects({
      event,
      showNoneOption: !this.customerId,
      selectedProjectId: this.project?.id ?? this.customer?.projectId,
      callback: async (id: string, name: string, object: any) => {
        // Don't show warning if it's a new customer or the project didn't change
        if (!this.customerId || (this.customer && id === this.customer.projectId)) {
          this.project = object;

          if (this.customerId) {
            this.save();
          }

          return;
        }

        // Show warning before changing project
        const alert = await this.alertCtrl.create({
          header: 'Change Project?',
          message:
            "Changing the project will update all conversations associated with this customer. Tags from the previous project that don't exist in the new project will be removed from these conversations.",
          buttons: [
            {
              text: 'Cancel',
              role: 'cancel',
            },
            {
              text: 'Change Project',
              handler: () => {
                this.project = object;

                if (this.customerId) {
                  this.save();
                }
              },
            },
          ],
        });

        await alert.present();
      },
    });
  }

  async editEmailConfiguration() {
    const modal = await this.modalCtrl.create({
      component: EditEmailChannelComponent,
      componentProps: {
        customerId: this.customerId,
      },
      cssClass: 'modal-medium',
    });

    modal.onDidDismiss().then(() => {
      this.getCustomer();
    });

    await modal.present();
  }

  async editInAppChatConfiguration() {
    const modal = await this.modalCtrl.create({
      component: EditWidgetChannelComponent,
      componentProps: {
        customerId: this.customerId,
      },
      cssClass: 'modal-large',
    });

    modal.onDidDismiss().then(() => {
      this.getCustomer();
    });

    await modal.present();
  }

  async editMicrosoftTeamsConfiguration() {
    const modal = await this.modalCtrl.create({
      component: EditMsTeamsChannelComponent,
      componentProps: {
        customerId: this.customerId,
      },
      cssClass: 'modal-medium',
    });

    modal.onDidDismiss().then(() => {
      this.getCustomer();
    });

    await modal.present();
  }
}
